import { camelizeKeys } from 'humps';
import { schema } from 'normalizr';
import moment from 'moment';
import defaults from 'lodash/defaults';
import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';

export const SESSION_STATUS_CLOSED = 'closed';
export const SESSION_STATUS_OPEN = 'open';

export const ENTITIES_KEY_SESSION = 'sessions';
export const ENTITIES_KEY_SESSION_DRAFT = 'sessionDrafts';

export const VIRTUAL_ATTENDANCE_TYPE = 'virtual';
export const IN_PERSON_ATTENDANCE_TYPE = 'in-person';

export const FIELD_SESSION_ATTENDEE_COUNT = 'attendeeCount';
export const FIELD_SESSION_CAPACITY = 'capacity';
export const FIELD_SESSION_CLOSE_AT_CAPACITY = 'closeAtCapacity';
export const FIELD_SESSION_DESCRIPTION = 'description';
export const FIELD_SESSION_END = 'end';
export const FIELD_SESSION_END_DATE = 'end_date';
export const FIELD_SESSION_END_TIME = 'end_time';
export const FIELD_SESSION_ID = 'id';
export const FIELD_SESSION_IS_DELETED = 'isDeleted';
export const FIELD_SESSION_LAT = 'lat';
export const FIELD_SESSION_LNG = 'lng';
export const FIELD_SESSION_LOCATION = 'location';
export const FIELD_SESSION_NAME = 'name';
export const FIELD_SESSION_REGISTRATION_REQUIRED = 'registrationRequired';
export const FIELD_SESSION_ROOM_NAME = 'roomName';
export const FIELD_SESSION_SESSION_CLOSE_SETTING = 'sessionCloseSetting';
export const FIELD_SESSION_START = 'start';
export const FIELD_SESSION_START_DATE = 'start_date';
export const FIELD_SESSION_START_TIME = 'start_time';
export const FIELD_SESSION_STATUS = 'status';
export const FIELD_SESSION_TAGS = 'tags';
export const FIELD_SESSION_VENUE_NAME = 'venueName';
export const FIELD_SESSION_VENUE_SAME_AS_EVENT = 'venueSameAsEvent';
export const FIELD_SESSION_WAITLIST = 'waitlist';
export const FIELD_SESSION_EVENT_VENUE_NAME = 'eventVenueName';
export const FIELD_SESSION_TITLE = 'title';
export const FIELD_SPEAKER_FIRST_NAME = 'first_name';
export const FIELD_SPEAKER_LAST_NAME = 'last_name';
export const FIELD_SPEAKER_AVATAR_URL = 'image_url';
export const FIELD_SPEAKER_BIO = 'bio';
export const FIELD_SPEAKER_JOB_TITLE = 'job_title';
export const FIELD_SPEAKER_COMPANY = 'company';

/**
 * Default values for a session.
 */
export const SESSION_DEFAULTS = {
    [FIELD_SESSION_ATTENDEE_COUNT]: null,
    [FIELD_SESSION_CAPACITY]: null,
    [FIELD_SESSION_CLOSE_AT_CAPACITY]: false,
    [FIELD_SESSION_DESCRIPTION]: '',
    [FIELD_SESSION_END]: null,
    [FIELD_SESSION_END_DATE]: null,
    [FIELD_SESSION_END_TIME]: null,
    [FIELD_SESSION_IS_DELETED]: false,
    [FIELD_SESSION_LAT]: null,
    [FIELD_SESSION_LNG]: null,
    [FIELD_SESSION_LOCATION]: '',
    [FIELD_SESSION_NAME]: '',
    [FIELD_SESSION_ROOM_NAME]: '',
    [FIELD_SESSION_SESSION_CLOSE_SETTING]: null,
    [FIELD_SESSION_START]: null,
    [FIELD_SESSION_START_DATE]: null,
    [FIELD_SESSION_START_TIME]: null,
    [FIELD_SESSION_STATUS]: 'open',
    [FIELD_SESSION_TAGS]: [],
    [FIELD_SESSION_VENUE_NAME]: '',
    [FIELD_SESSION_VENUE_SAME_AS_EVENT]: true,
    [FIELD_SESSION_WAITLIST]: false,
    [FIELD_SESSION_EVENT_VENUE_NAME]: null,
    [FIELD_SPEAKER_FIRST_NAME]: null,
    [FIELD_SPEAKER_LAST_NAME]: null,
    [FIELD_SPEAKER_AVATAR_URL]: null,
    [FIELD_SPEAKER_BIO]: null,
    [FIELD_SPEAKER_JOB_TITLE]: null,
    [FIELD_SPEAKER_COMPANY]: null,
    [FIELD_SESSION_REGISTRATION_REQUIRED]: false,
};

export const SESSION_CSV_IMPORT_FIELDS = [
    { value: FIELD_SESSION_NAME, label: 'Name' },
    { value: FIELD_SESSION_START_DATE, label: 'Start Date' },
    { value: FIELD_SESSION_START_TIME, label: 'Start Time' },
    { value: FIELD_SESSION_END_TIME, label: 'End Time' },
    { value: FIELD_SESSION_END_DATE, label: 'End Date' },
    { value: FIELD_SESSION_CAPACITY, label: 'Capacity' },
    { value: FIELD_SESSION_ROOM_NAME, label: 'Room Name' },
    { value: FIELD_SESSION_TAGS, label: 'Tags' },
    { value: FIELD_SESSION_DESCRIPTION, label: 'Description' },
    { value: FIELD_SESSION_VENUE_NAME, label: 'Venue Name' },
];

export const VIRTUAL_SESSION_CSV_IMPORT_FIELDS = [
    { value: FIELD_SESSION_TITLE, label: 'Title' },
    { value: FIELD_SESSION_START_DATE, label: 'Start Date' },
    { value: FIELD_SESSION_START_TIME, label: 'Start Time' },
    { value: FIELD_SESSION_END_TIME, label: 'End Time' },
    { value: FIELD_SESSION_END_DATE, label: 'End Date' },
    { value: FIELD_SESSION_TAGS, label: 'Tags' },
    { value: FIELD_SESSION_DESCRIPTION, label: 'Description' },
    { value: FIELD_SESSION_REGISTRATION_REQUIRED, label: 'Registration Required' },
    { value: FIELD_SESSION_CAPACITY, label: 'Capacity' },
    { value: FIELD_SESSION_WAITLIST, label: 'Waitlist' },
    { value: FIELD_SESSION_LOCATION, label: 'Location' },
    { value: FIELD_SESSION_STATUS, label: 'Status' },
];

export const VIRTUAL_SESSION_SPEAKERS_COLUMNS = [
    { value: FIELD_SPEAKER_FIRST_NAME, label: 'First name' },
    { value: FIELD_SPEAKER_LAST_NAME, label: 'Last name' },
    { value: FIELD_SPEAKER_AVATAR_URL, label: 'Image URL' },
    { value: FIELD_SPEAKER_BIO, label: 'Bio' },
    { value: FIELD_SPEAKER_JOB_TITLE, label: 'Job title' },
    { value: FIELD_SPEAKER_COMPANY, label: 'Company' },
];

export const SESSION_CSV_IMPORT__REQUIRED_FIELDS = [
    FIELD_SESSION_END_DATE,
    FIELD_SESSION_START_DATE,
    FIELD_SESSION_NAME,
];

export const VIRTUAL_SESSION_CSV_IMPORT__REQUIRED_FIELDS = [
    FIELD_SESSION_TITLE,
    FIELD_SESSION_DESCRIPTION,
    FIELD_SESSION_END_DATE,
    FIELD_SESSION_START_DATE,
    FIELD_SESSION_TITLE,
];

export const SESSION_DATE_FORMAT = 'MM/DD/YYYY HH:mm';

export const processSession = (sessionData) => {
    const session = defaults(camelizeKeys(sessionData), cloneDeep(SESSION_DEFAULTS));

    // Convert the session ID to a string so that it can be treated uniformly in
    // components. We have string IDs when we create new, un-persisted session
    // entities.
    if (session.id) {
        session.id = session.id.toString();
    }

    if (session.start) {
        const date = moment(session.start);
        session.start = date.parseZone().format(SESSION_DATE_FORMAT);
    }

    if (session.end) {
        const date = moment(session.end);
        session.end = date.parseZone().format(SESSION_DATE_FORMAT);
    }

    if (session.capacity) {
        session.closeAtCapacity = true;
        if (Number.isFinite(Number(session.capacity))) {
            session.capacity = session.capacity.toString();
        }
    }
    if (session.event && session.event.venueName) {
        session.eventVenueName = session.event.venueName;
        delete session.event;
    }

    return session;
};

export const sessionSchema = new schema.Entity(
    ENTITIES_KEY_SESSION,
    {},
    {
        processStrategy: (entity) => processSession(entity),
    },
);

export const sessionsSchema = [sessionSchema];

/**
 * Session drafts are very similar to sessions in structure. Drafts are either
 * newly created sessions that have not been persisted to the server (no ID)
 * or edits to existing sessions.
 *
 * We store this as a separate entity so that we can easily discard the draft
 * without affecting the original entity and treat edits/creates uniformly.
 */
export const SESSION_DRAFT_DEFAULTS = merge(cloneDeep(SESSION_DEFAULTS), {
    isDirty: false,
});

export const processSessionDraft = (sessionDraftData) =>
    defaults(camelizeKeys(sessionDraftData), cloneDeep(SESSION_DRAFT_DEFAULTS));

export const sessionDraftSchema = new schema.Entity(
    ENTITIES_KEY_SESSION_DRAFT,
    {},
    {
        processStrategy: (entity) => processSessionDraft(entity),
    },
);

export const sessionDraftsSchema = [sessionDraftSchema];

export const getColumnAssociationFunction = (header) => {
    const lowerHeader = header.toLowerCase();
    if (['room name', 'room_name', 'roomname', 'room'].indexOf(lowerHeader) !== -1) {
        return FIELD_SESSION_ROOM_NAME;
    }
    if (['venue name', 'venue_name', 'venuename', 'venue'].indexOf(lowerHeader) !== -1) {
        return FIELD_SESSION_VENUE_NAME;
    }

    if (['registration required'].indexOf(lowerHeader) !== -1) {
        return FIELD_SESSION_REGISTRATION_REQUIRED;
    }

    return undefined;
};
